import React, { useEffect, useRef } from 'react';
import { Animated, TextInput } from 'react-native';

import { Box, Text } from '@components/Restyle/index';
import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

export const AddMembersInput = ({
  focusOnSearch,
  setFocusOnSearch,
  searchVal,
  setText,
  inputRef,
  placeholder = 'Add assignee by email, name or group',
}: {
  focusOnSearch: boolean;
  setFocusOnSearch: (focus: boolean) => void;
  searchVal: string;
  setText: (text: string) => void;
  inputRef: React.RefObject<TextInput | null>;
  placeholder?: string;
}) => {
  // Animated value to control transition between placeholder and input
  const toggleAnim = useRef(new Animated.Value(focusOnSearch ? 1 : 0)).current;

  useEffect(() => {
    // Run animation whenever foucsOnSearch changes
    Animated.timing(toggleAnim, {
      toValue: focusOnSearch ? 1 : 0,
      duration: 200, // animation duration in ms
      useNativeDriver: false, // layout properties (width/opacity on web) need false
    }).start();
  }, [focusOnSearch, toggleAnim]);

  // Placeholder fades out when focusOnSearch = true
  const placeholderOpacity = toggleAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0],
  });

  // Search input fades in when focusOnSearch = true
  const inputOpacity = toggleAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  // Slight horizontal scale for the input when it appears
  const inputScaleX = toggleAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.95, 1],
  });

  return (
    <Box
      my='xs'
      flexDirection='row'
      alignItems='center'
      justifyContent='center'
      marginTop='s'
      marginBottom='s'
      borderColor={focusOnSearch ? 'black' : 'grey02'}
      borderWidth={1}
      accessibilityLabel='Search Members Field'
      borderRadius='xs'
      height={48}>
      {/* Placeholder text (tap to start searching) */}
      {!focusOnSearch && (
        <Animated.View
          style={{
            flex: 1,
            opacity: placeholderOpacity, // fade out animation
          }}>
          <Text
            marginHorizontal='m'
            onPress={() => {
              setFocusOnSearch(true);
            }}
            flex={1}
            variant='webBodySecondary'
            color='grey04'>
            {placeholder}
          </Text>
        </Animated.View>
      )}

      {/* Search input with close icon */}
      {focusOnSearch && (
        <Animated.View
          // Animated wrapper for the input row
          style={{
            flexDirection: 'row',
            marginHorizontal: 16, // equals mx="m"
            opacity: inputOpacity, // fade in animation
            transform: [{ scaleX: inputScaleX }], // subtle scale animation
          }}>
          <TextInput
            accessibilityLabel='Search member textfield'
            placeholder={placeholder}
            placeholderTextColor={theme.colors.grey04}
            ref={inputRef}
            style={{
              outlineStyle: 'none',
              width: 696,
              height: 24,
            }}
            multiline={false}
            autoFocus={true}
            numberOfLines={0}
            maxLength={255}
            // placeholder="Add members by email, name or group"
            // placeholderTextColor="#828282"
            returnKeyType='default'
            value={searchVal}
            onChangeText={setText}
            onBlur={() => {
              // If user leaves the input and it's empty, go back to placeholder mode
              if (searchVal.length === 0) setFocusOnSearch(false);
            }}
            blurOnSubmit={false}
          />
          <Icon
            accessibilityLabel='Close'
            marginLeft='xs'
            name='X'
            variant='l'
            disabled={searchVal.length === 0}
            color={searchVal.length > 0 ? 'black' : 'white'}
            onPress={() => {
              if (searchVal.length === 0) return;
              // Clear text and return to placeholder mode
              setFocusOnSearch(false);
              setText('');
            }}
          />
        </Animated.View>
      )}
    </Box>
  );
};
